import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarClock, UtensilsCrossed, MapPin, Check, Plus, Trash2, ImagePlus,
  Loader2, ExternalLink, Globe, Facebook, Instagram, Clock, Tag, Eye, LayoutDashboard,
  Scissors, Wrench, BedDouble, Stethoscope, Dumbbell, Car, GraduationCap, Building2, Puzzle,
  ChevronUp, ChevronDown, ChevronRight, Play, Pause, CreditCard, Wallet, QrCode, Download, Copy, X, Printer,
  Store, BarChart3, Bell, Home, ClipboardList, Settings, HelpCircle, MessageSquare, Users, ShieldCheck,
} from 'lucide-react';
import { pluginsFor, pluginEnabled } from '../data/businessCatalog';
import { pageVars } from '../data/motifs';
import { Section } from '../components/business/TemplateFields';
import ReservationsEditor from '../components/business/ReservationsEditor';
import MenuEditor from '../components/business/MenuEditor';
import BusinessCardEditor from '../components/business/BusinessCardEditor';
import { BusinessDashboardContext } from '../components/business/dashboard/dashboardContext';
import OverviewSection from '../components/business/dashboard/OverviewSection';
import OrdersSection from '../components/business/dashboard/OrdersSection';
import QrPanel from '../components/business/dashboard/QrPanel';
import StatsSection from '../components/business/dashboard/StatsSection';
import SettingsSection from '../components/business/dashboard/SettingsSection';
import TemplatePicker from '../components/business/dashboard/TemplatePicker';
import BusinessTeamSection from '../components/business/BusinessTeamSection';
import BusinessTeamBanner from '../components/business/BusinessTeamBanner';
import CompanyFooter from '../components/CompanyFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const SPEC_COLORS = ['#f59e0b', '#38bdf8', '#a78bfa', '#34d399', '#f472b6', '#fb7185', '#facc15', '#22d3ee'];

const TEMPLATES = [
  { id: 'reservations', template: 'reservations', kind: 'general', icon: CalendarClock, title: 'Rezervācijas & Pasākumi', desc: 'VR istabas, izklaide, meistarklases — kalendārs, laiku rezervācija, cenas, dāvanu kartes.', ready: true, accent: '#FF9F1C' },
  { id: 'menu', template: 'menu', icon: UtensilsCrossed, title: 'Digitālā ēdienkarte & Galdiņi', desc: 'Restorāniem un kafejnīcām — ēdienkarte ar kategorijām, galdiņu rezervācija, dienas piedāvājumi.', ready: true, accent: '#2EC4B6' },
  { id: 'business_card', template: 'business_card', icon: MapPin, title: 'Vizītkarte & Lokālais info', desc: 'Universāla reklāma — darba laiki, navigācija kartē, kontaktu pogas, akcijas, galerija.', ready: true, accent: '#8B5CF6' },
  // Business-type presets — all powered by the reservations engine (services + prices
  // + online booking / request), with type-specific labels and starter content.
  { id: 'salon', template: 'reservations', kind: 'salon', icon: Scissors, title: 'Skaistumkopšana & Salons', desc: 'Frizieri, manikīrs, kosmetologi — pakalpojumi, cenas un pieraksts vizītei tiešsaistē.', ready: true, accent: '#EC4899',
    preset: { tagline: 'Pieraksties vizītei tiešsaistē', services: [{ name: 'Sieviešu matu griezums', price: 25, unit: 'package', duration_min: 60 }, { name: 'Manikīrs', price: 20, unit: 'package', duration_min: 45 }] } },
  { id: 'health', template: 'reservations', kind: 'health', icon: Stethoscope, title: 'Veselība & Klīnika', desc: 'Zobārsti, ārsti, terapeiti — pakalpojumi un pieraksts pie speciālista.', ready: true, accent: '#0EA5E9',
    preset: { tagline: 'Piesakies vizītei pie speciālista', services: [{ name: 'Konsultācija', price: 30, unit: 'package', duration_min: 30 }] } },
  { id: 'fitness', template: 'reservations', kind: 'fitness', icon: Dumbbell, title: 'Sports & Fitness', desc: 'Sporta zāles, treneri, nodarbības — grafiks un pieteikšanās nodarbībai.', ready: true, accent: '#22C55E',
    preset: { tagline: 'Piesakies nodarbībai', services: [{ name: 'Personīgā treniņa stunda', price: 25, unit: 'hour', duration_min: 60 }, { name: 'Grupu nodarbība', price: 8, unit: 'group', duration_min: 60 }] } },
  { id: 'auto', template: 'reservations', kind: 'auto', icon: Car, title: 'Auto serviss', desc: 'Remonts, riepu maiņa, diagnostika — pakalpojumi un pieraksts servisā.', ready: true, accent: '#F97316',
    preset: { tagline: 'Piesaki auto servisam', services: [{ name: 'Riepu maiņa', price: 20, unit: 'package', duration_min: 30 }, { name: 'Eļļas maiņa', price: 40, unit: 'package', duration_min: 45 }] } },
  { id: 'education', template: 'reservations', kind: 'education', icon: GraduationCap, title: 'Izglītība & Kursi', desc: 'Kursi, meistarklases, privātstundas — saraksts un pieteikšanās.', ready: true, accent: '#8B5CF6',
    preset: { tagline: 'Piesakies kursam', services: [{ name: 'Privātstunda', price: 15, unit: 'hour', duration_min: 60 }] } },
  { id: 'stay', template: 'reservations', kind: 'stay', icon: BedDouble, title: 'Naktsmītne & Brīvdienu māja', desc: 'Viesu nami, brīvdienu mājas, pirtis — cenas un rezervācijas pieprasījums.', ready: true, accent: '#14B8A6', requestOnly: true,
    preset: { request_only: true, tagline: 'Rezervē savu uzturēšanos', services: [{ name: 'Nakts standarta numurā', price: 45, unit: 'package', duration_min: 0 }] } },
  { id: 'trade', template: 'reservations', kind: 'trade', icon: Wrench, title: 'Pakalpojumi & Amatnieks', desc: 'Santehniķi, elektriķi, celtnieki — cenrādis un pieprasīt piedāvājumu/atzvanu.', ready: true, accent: '#EAB308', requestOnly: true,
    preset: { request_only: true, tagline: 'Pieprasi piedāvājumu', services: [{ name: 'Izsaukums un diagnostika', price: 20, unit: 'package', duration_min: 0 }] } },
  { id: 'realestate', template: 'reservations', kind: 'realestate', icon: Building2, title: 'Nekustamais īpašums', desc: 'Aģentūras un mākleri — objektu piedāvājumi un pieprasīt apskati/kontaktu.', ready: true, accent: '#64748B', requestOnly: true,
    preset: { request_only: true, tagline: 'Piesaki objekta apskati' } },
  // --- Special section (grown over time) — flexible pages where the owner
  // toggles the modules they want. Phase 1 "light" is powered by the
  // reservations engine (widest module set); menu-ordering + tables arrive in
  // Phase 2. See /app/memory/kolv_base/SPECIAL_PROGRESS.md.
  { id: 'blank_basic', template: 'reservations', kind: 'special', group: 'special', icon: Puzzle, title: 'Blank basic',
    desc: 'Universāla lapa — sāc ar nelielu paraugu (Par mums + Kontakti) un ieslēdz pārējos moduļus: rezervācijas, ēdienkarte, galdiņi, pakalpojumi, dāvanu kartes, galerija, akcijas, atsauksmes, BUJ.', ready: true, accent: '#A78BFA',
    preset: {
      tagline: 'Laipni lūdzam! Šī ir mūsu jaunā lapa.',
      plugins: { about: true, contacts: true },
      about: 'Īsi pastāsti par savu uzņēmumu — ko piedāvā, kas tevi padara īpašu un kāpēc klientiem izvēlēties tieši tevi. Aizstāj šo tekstu ar savu stāstu.',
      contacts: { phone: '+371 20000000', email: 'info@uznemums.lv' },
    } },
];

// CRM dashboard sections (shown as desktop tabs + a mobile bottom bar).
const SECTIONS = [
  { id: 'overview', label: 'Sākums', icon: Home },
  { id: 'orders', label: 'Pasūtījumi', icon: ClipboardList },
  { id: 'store', label: 'Veikals', icon: Store },
  { id: 'stats', label: 'Statistika', icon: BarChart3 },
  { id: 'team', label: 'Komanda', icon: Users },
  { id: 'settings', label: 'Iestatījumi', icon: Settings },
];

const STORE_TABS = [
  { id: 'identity', label: 'Identitāte', icon: QrCode },
  { id: 'build', label: 'Uzbūve', icon: Puzzle },
  { id: 'content', label: 'Saturs', icon: LayoutDashboard },
];

export default function BusinessTemplatesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Admin "god-mode": when an admin opens ?admin_biz=<userId>, every owner-scoped
  // request on this page is transparently scoped to that business via a ?biz= param.
  const adminBiz = React.useMemo(() => new URLSearchParams(window.location.search).get('admin_biz') || null, []);
  const adminName = React.useMemo(() => new URLSearchParams(window.location.search).get('admin_name') || null, []);
  const adminIntRef = useRef(null);
  if (adminBiz && adminIntRef.current === null) {
    adminIntRef.current = axios.interceptors.request.use((config) => {
      const u = config.url || '';
      if (u.includes('/api/business-page') || u.includes('/api/business-team')) {
        config.params = { ...(config.params || {}), biz: adminBiz };
      }
      return config;
    });
  }
  useEffect(() => () => {
    if (adminIntRef.current !== null) { axios.interceptors.request.eject(adminIntRef.current); adminIntRef.current = null; }
  }, []);
  const [cfg, setCfg] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [dayDate, setDayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dayView, setDayView] = useState(null);
  const [calMode, setCalMode] = useState('day');
  const [weekView, setWeekView] = useState(null);
  const [specFilter, setSpecFilter] = useState('');
  const [tab, setTab] = useState(null);
  const [storeTab, setStoreTab] = useState('identity');
  const [loadStart, setLoadStart] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); });
  const [loadEnd, setLoadEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [loadData, setLoadData] = useState(null);
  const [accounting, setAccounting] = useState(null);
  const [onsiteBooking, setOnsiteBooking] = useState(null);
  const [onsiteAmount, setOnsiteAmount] = useState('');
  const [onsiteMethod, setOnsiteMethod] = useState('onsite');
  const [onsiteBusy, setOnsiteBusy] = useState(false);
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleChecking, setSettleChecking] = useState(false);
  // Clickable stat detail (modal) + QR deep-link
  const [statDetail, setStatDetail] = useState(null); // 'views' | 'upcoming' | 'pending' | 'total'
  const [detailLoading, setDetailLoading] = useState(false);
  const [allBookings, setAllBookings] = useState([]);
  const [viewsDetail, setViewsDetail] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [panelSurface, setPanelSurface] = useState('melnais');
  const [panelAccent, setPanelAccent] = useState('amber_warmth');
  const [dailyDate, setDailyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dailySummary, setDailySummary] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [soldGiftCards, setSoldGiftCards] = useState([]);
  const [giftCardsInfo, setGiftCardsInfo] = useState({ total_value: 0, active_count: 0, count: 0, partial_enabled: false });
  const [footerOwner, setFooterOwner] = useState(null);  // enriched contacts+requisites for the dashboard footer preview

  const mondayOf = (iso) => {
    const dt = new Date(iso + 'T00:00:00');
    const dow = (dt.getDay() + 6) % 7; // 0 = Monday
    dt.setDate(dt.getDate() - dow);
    return dt.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (!user) { navigate('/welcome'); return; }
    axios.get(`${API}/business-page/me`).then(({ data }) => {
      setCfg(data);
      setPanelSurface(data.panel_surface || 'melnais');
      setPanelAccent(data.panel_accent || 'amber_warmth');
    }).catch(() => {});
    axios.get(`${API}/business-page/me/pages`).then(({ data }) => setPages(data.pages || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  const savePanelTheme = async (next) => {
    const surface = next.surface ?? panelSurface;
    const accent = next.accent ?? panelAccent;
    setPanelSurface(surface); setPanelAccent(accent);
    try { await axios.put(`${API}/business-page/me`, { panel_surface: surface, panel_accent: accent }); }
    catch (e) { toast.error('Neizdevās saglabāt paneļa motīvu'); }
  };

  const reloadPages = async () => {
    try { const { data } = await axios.get(`${API}/business-page/me/pages`); setPages(data.pages || []); return data.pages || []; }
    catch (e) { return pages; }
  };

  // Auto-select a page when arriving from the profile's per-page "Pārvaldīt" (?page=id).
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || pages.length === 0) return;
    const pid = new URLSearchParams(window.location.search).get('page');
    if (!pid) return;
    const pg = pages.find((p) => p.id === pid);
    if (pg) {
      autoSelectedRef.current = true;
      const s = initialSectionRef.current;
      selectPage(pg, (s && ['overview', 'orders', 'store', 'stats', 'settings'].includes(s)) ? s : 'overview');
    }
  }, [pages]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadDay = async (d) => {
    try { const { data } = await axios.get(`${API}/business-page/bookings/day`, { params: { date: d, page_id: cfg?.id } }); setDayView(data); }
    catch (e) { setDayView(null); }
  };
  const loadWeek = async (d) => {
    try { const { data } = await axios.get(`${API}/business-page/bookings/week`, { params: { start: mondayOf(d), page_id: cfg?.id } }); setWeekView(data); }
    catch (e) { setWeekView(null); }
  };
  useEffect(() => {
    if (!user) return;
    if (calMode === 'day') loadDay(dayDate); else loadWeek(dayDate);
  }, [user, dayDate, calMode, cfg?.id]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/business-page/stats/my`, { params: { page_id: cfg?.id } }).then(({ data }) => setStats(data)).catch(() => {});
  }, [user, cfg?.id]);

  const refreshAccounting = async () => {
    try { const { data } = await axios.get(`${API}/business-page/accounting/me`, { params: { page_id: cfg?.id } }); setAccounting(data); }
    catch (e) { setAccounting(null); }
  };
  const [sellerAcct, setSellerAcct] = useState(null);
  const refreshSellerAcct = async () => {
    try { const { data } = await axios.get(`${API}/business-page/accounting/me`); setSellerAcct(data); }
    catch (e) { setSellerAcct(null); }
  };
  useEffect(() => { if (user) refreshSellerAcct(); }, [user, pages.length]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || !cfg?.id) { setAccounting(null); return; }
    refreshAccounting();
  }, [user, cfg?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Returned from a Stripe commission settlement.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (params.get('settle_cancel')) {
      toast.error('Norēķins atcelts');
      window.history.replaceState({}, '', '/business-templates');
      return;
    }
    if (!params.get('settle') || !sid) return;
    setSettleChecking(true);
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (data.payment_status === 'paid') {
          setSettleChecking(false);
          toast.success('KoLV komisijas parāds nokārtots!');
          window.history.replaceState({}, '', '/business-templates');
          refreshAccounting();
          return;
        }
      } catch { /* keep polling */ }
      attempt += 1;
      if (attempt >= 8) { setSettleChecking(false); window.history.replaceState({}, '', '/business-templates'); refreshAccounting(); return; }
      setTimeout(poll, 1500);
    };
    poll();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const submitOnsite = async () => {
    const amt = parseFloat(onsiteAmount);
    if (!onsiteBooking || !(amt > 0)) { toast.error('Ievadi summu (> €0)'); return; }
    setOnsiteBusy(true);
    try {
      await axios.post(`${API}/business-page/bookings/${onsiteBooking.id}/mark-onsite`, { amount: amt, method: onsiteMethod });
      toast.success(`Atzīmēts kā apmaksāts · KoLV komisija €${(amt * 0.1).toFixed(2)}`);
      setOnsiteBooking(null); setOnsiteAmount(''); setOnsiteMethod('onsite');
      await Promise.all([refreshAccounting(), refetchStats(), refreshSellerAcct()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Neizdevās'); }
    finally { setOnsiteBusy(false); }
  };

  const settleDebt = async (method) => {
    if (!accounting || accounting.owed <= 0) return;
    setSettleBusy(true);
    try {
      const { data } = await axios.post(`${API}/business-page/accounting/settle`, {
        page_id: cfg?.id, method, origin_url: window.location.origin,
      });
      if (method === 'stripe' && data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(`Parāds nokārtots (€${(data.settled ?? 0).toFixed(2)})`);
      await Promise.all([refreshAccounting(), refreshSellerAcct()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Neizdevās nokārtot'); }
    finally { setSettleBusy(false); }
  };

  const specialistCount = (cfg?.reservations?.specialists || []).length;
  useEffect(() => {
    if (!user || !cfg?.template || specialistCount === 0) { setLoadData(null); return; }
    axios.get(`${API}/business-page/bookings/specialist-load`, { params: { start: loadStart, end: loadEnd, page_id: cfg?.id } })
      .then(({ data }) => setLoadData(data)).catch(() => setLoadData(null));
  }, [user, cfg?.template, cfg?.id, specialistCount, loadStart, loadEnd]);
  const exportLoad = async (fmt) => {
    try {
      const { data } = await axios.get(`${API}/business-page/bookings/specialist-load/export`, { params: { start: loadStart, end: loadEnd, fmt, page_id: cfg?.id }, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url; a.download = `specialistu-noslodze-${loadStart}_${loadEnd}.${fmt === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Eksports neizdevās'); }
  };
  const shiftDay = (delta) => {
    const step = calMode === 'week' ? delta * 7 : delta;
    const dt = new Date(dayDate + 'T00:00:00'); dt.setDate(dt.getDate() + step);
    setDayDate(dt.toISOString().slice(0, 10));
  };

  const rc = cfg?.reservations || { tagline: '', cover_url: '', services: [], specialists: [], gift_cards: [], gallery: [], time_slots: [] };
  const mc = cfg?.menu || { tagline: '', cover_url: '', daily_special: '', categories: [], gallery: [], reservations_enabled: false, time_slots: [], contact_note: '' };
  const bc = cfg?.business_card || { tagline: '', cover_url: '', about: '', address: '', lat: null, lng: null, working_hours: '', contacts: {}, promotions: [], gallery: [] };
  const setRc = (patch) => setCfg((c) => ({ ...c, reservations: { ...rc, ...patch } }));
  const setMc = (patch) => setCfg((c) => ({ ...c, menu: { ...mc, ...patch } }));
  const setBc = (patch) => setCfg((c) => ({ ...c, business_card: { ...bc, ...patch } }));
  const specColor = (id) => {
    if (!id) return null;
    const idx = (rc.specialists || []).findIndex((s) => s.id === id);
    return idx >= 0 ? SPEC_COLORS[idx % SPEC_COLORS.length] : null;
  };
  const availPlugins = cfg?.template ? pluginsFor(cfg.template, rc.kind || 'general') : [];
  const curCfg = cfg?.template === 'menu' ? mc : cfg?.template === 'business_card' ? bc : rc;
  const setCur = cfg?.template === 'menu' ? setMc : cfg?.template === 'business_card' ? setBc : setRc;
  const pluginOn = (id) => availPlugins.includes(id) && pluginEnabled(cfg?.template, curCfg.plugins, id, curCfg.kind);
  const togglePlugin = (id) => setCur({ plugins: { ...(curCfg.plugins || {}), [id]: !pluginOn(id) } });
  const matchSpec = (b) => !specFilter || b.specialist_id === specFilter;
  const upcomingFiltered = (stats?.upcoming || []).filter(matchSpec);
  const dayFilteredSlots = dayView ? dayView.slots.map((s) => ({ ...s, bookings: s.bookings.filter(matchSpec) })) : null;
  const dayTotal = dayFilteredSlots ? dayFilteredSlots.reduce((n, s) => n + s.bookings.length, 0) : 0;
  const weekFilteredDays = weekView ? weekView.days.map((d) => { const bk = d.bookings.filter(matchSpec); return { ...d, bookings: bk, count: bk.length }; }) : null;
  const weekTotal = weekFilteredDays ? weekFilteredDays.reduce((n, d) => n + d.count, 0) : 0;
  const activeTab = tab || 'type';

  const li = cfg?.legal_info || { legal_name: '', entity_type: '', registration_number: '', vat_number: '', legal_address: '' };
  const legalSame = cfg?.legal_same_as_shop !== false;

  const payload = () => ({ template: cfg?.template, display_name: cfg?.display_name || '', display_logo_url: cfg?.display_logo_url || '', facebook: cfg?.facebook || '', instagram: cfg?.instagram || '', tiktok: cfg?.tiktok || '', youtube: cfg?.youtube || '', whatsapp: cfg?.whatsapp || '', contact_phone: cfg?.contact_phone || '', contact_email: cfg?.contact_email || '', contact_website: cfg?.contact_website || '', address: cfg?.address || bc.address || '', lat: cfg?.lat ?? bc.lat ?? null, lng: cfg?.lng ?? bc.lng ?? null, reservations: rc, menu: mc, business_card: bc, legal_same_as_shop: legalSame, legal_info: li });

  // Human label for a page (matches its template + reservations kind).
  const templateLabel = (page) => {
    if (!page?.template) return 'Lapa';
    const k = page.kind || (page.reservations || {}).kind || 'general';
    const m = TEMPLATES.find((t) => t.template === page.template && (page.template !== 'reservations' || (t.kind || 'general') === k));
    return m?.title || (page.template === 'menu' ? 'Ēdienkarte' : page.template === 'business_card' ? 'Vizītkarte' : 'Rezervācijas');
  };

  // Open a page in the manage editor (each page has its own config).
  const selectPage = (page, sec = 'overview') => {
    setCfg(page);
    setStats(null);
    setTab('manage');
    setSection(sec);
    try {
      if (page?.id) {
        const u = new URL(window.location.href);
        u.searchParams.set('page', page.id);
        window.history.replaceState(window.history.state, '', u.toString());
      }
    } catch (e) { /* noop */ }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Activate a template card by CREATING A NEW PAGE (multiple allowed, even of
  // the same type). Reservations-family cards carry a kind + starter preset.
  const activateTpl = async (card) => {
    setSaving(true);
    try {
      const body = {
        template: card.template,
        kind: card.template === 'reservations' ? (card.kind || 'general') : 'general',
      };
      if (card.template === 'reservations' && card.preset) {
        body.preset = { ...card.preset, request_only: !!card.requestOnly };
      }
      const { data: page } = await axios.post(`${API}/business-page/me/pages`, body);
      await reloadPages();
      toast.success('Lapa izveidota! Aizpildi informāciju.');
      selectPage(page);
    } catch (e) { toast.error('Neizdevās izveidot lapu'); } finally { setSaving(false); }
  };

  const save = async () => {
    if (!cfg?.id) { toast.error('Vispirms izvēlies lapu'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/business-page/me/pages/${cfg.id}`, payload());
      setCfg(data);
      await reloadPages();
      toast.success('Saglabāts!');
    } catch (e) { toast.error('Neizdevās saglabāt'); } finally { setSaving(false); }
  };

  const deletePage = async (page) => {
    if (!window.confirm(`Dzēst lapu "${page.display_name || 'Bez nosaukuma'}"? Šo nevar atsaukt.`)) return;
    try {
      await axios.delete(`${API}/business-page/me/pages/${page.id}`);
      if (cfg?.id === page.id) { setCfg(null); setTab('type'); }
      await reloadPages();
      toast.success('Lapa dzēsta');
    } catch (e) { toast.error('Neizdevās dzēst'); }
  };

  const regeneratePage = async () => {
    if (!cfg?.id) { toast.error('Vispirms izvēlies lapu'); return; }
    if (!cfg?.generated_from) { toast.error('Šai lapai nav avota saites'); return; }
    if (!window.confirm('Atjaunot šo lapu no avota saites? Pašreizējais saturs (pakalpojumi, cenas, attēli) tiks aizstāts ar jaunāko no avota vietnes.')) return;
    setRegenBusy(true);
    try {
      const { data } = await axios.post(`${API}/business-page/me/pages/${cfg.id}/regenerate`, {}, { timeout: 120000 });
      await reloadPages();
      selectPage(data.page, 'settings');
      toast.success('Lapa atjaunota no avota!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Neizdevās atjaunot lapu. Mēģini vēlreiz.');
    } finally { setRegenBusy(false); }
  };

  const pausePage = async (page) => {
    try {
      await axios.put(`${API}/business-page/me/pages/${page.id}/pause`, { paused: !page.is_paused });
      await reloadPages();
      toast.success(!page.is_paused ? 'Lapa pauzēta — paslēpta no kartes' : 'Lapa atkal aktīva');
    } catch (e) { toast.error('Neizdevās'); }
  };

  const movePage = async (idx, dir) => {
    const next = [...pages];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setPages(next);
    try { await axios.put(`${API}/business-page/me/pages/reorder`, { order: next.map((p) => p.id) }); }
    catch (e) { toast.error('Neizdevās pārkārtot'); await reloadPages(); }
  };

  const refetchStats = async () => {
    try { const { data } = await axios.get(`${API}/business-page/stats/my`, { params: { page_id: cfg?.id } }); setStats(data); } catch (e) { /* ignore */ }
  };

  // End-of-day summary (orders / revenue / top dishes) for the Statistika section.
  const fetchDailySummary = async (date) => {
    if (!cfg?.id) { setDailySummary(null); return; }
    setDailyLoading(true);
    try { const { data } = await axios.get(`${API}/business-page/daily-summary`, { params: { page_id: cfg.id, date } }); setDailySummary(data); }
    catch (e) { setDailySummary(null); }
    finally { setDailyLoading(false); }
  };

  const dailyEmailOn = cfg?.daily_summary_email !== false;
  const toggleDailyEmail = async () => {
    if (!cfg?.id) return;
    const next = !dailyEmailOn;
    setCfg((c) => ({ ...c, daily_summary_email: next }));
    try {
      await axios.put(`${API}/business-page/me/pages/${cfg.id}`, { daily_summary_email: next });
      toast.success(next ? 'Dienas pārskats tiks sūtīts e-pastā' : 'Automātiskais e-pasts izslēgts');
    } catch (e) { toast.error('Neizdevās saglabāt'); setCfg((c) => ({ ...c, daily_summary_email: !next })); }
  };

  // Sold gift cards (reservations pages) — list + manual "mark used" / partial deduction.
  const loadSoldGiftCards = async () => {
    if (!cfg?.id) { setSoldGiftCards([]); return; }
    try {
      const { data } = await axios.get(`${API}/business-page/gift-cards/sold`, { params: { page_id: cfg.id } });
      setSoldGiftCards(data.gift_cards || []);
      setGiftCardsInfo({ total_value: data.total_value || 0, active_count: data.active_count || 0, count: data.count || 0, partial_enabled: !!data.partial_enabled });
    } catch (e) { setSoldGiftCards([]); }
  };
  const redeemGiftCard = async (gid, body) => {
    try {
      const { data } = await axios.post(`${API}/business-page/gift-cards/${gid}/redeem`, body);
      setSoldGiftCards((list) => list.map((g) => g.id === gid ? { ...g, status: data.status, remaining: data.remaining } : g));
      toast.success(data.status === 'redeemed' ? 'Atzīmēta kā izmantota'
        : data.status === 'partial' ? `Norakstīts · atlikums €${Number(data.remaining).toFixed(2)}`
        : 'Atjaunota kā aktīva');
    } catch (e) { toast.error(e.response?.data?.detail || 'Neizdevās atjaunināt'); }
  };

  const setBookingStatus = async (bookingId, status) => {
    try {
      await axios.post(`${API}/business-page/bookings/${bookingId}/status`, { status });
      const msgs = { confirmed: 'Apstiprināts — klients informēts', rejected: 'Noraidīts — klients informēts', ready: 'Atzīmēts kā gatavs — klients informēts', completed: 'Atzīmēts kā izsniegts — klients informēts' };
      toast.success(msgs[status] || 'Atjaunināts');
      await refetchStats();
      if (activeTab === 'manage' && section === 'orders' && cfg?.id) {
        try { const { data } = await axios.get(`${API}/business-page/bookings/my`, { params: { page_id: cfg.id } }); setAllBookings(data.bookings || []); } catch (e) { /* ignore */ }
      }
      if (calMode === 'day') await loadDay(dayDate); else await loadWeek(dayDate);
    } catch (e) { toast.error('Neizdevās'); }
  };

  // Public deep-link + QR for the currently selected page. The ?src=qr tag lets
  // the dashboard attribute visits that arrive via this shared/scanned link.
  const publicPageUrl = (user && cfg?.id)
    ? `${window.location.origin}/uznemums/${user._id}/${cfg.id}?src=qr`
    : '';
  const qrSrc = (user && cfg?.id)
    ? `${API}/business-page/me/pages/${cfg.id}/qr?base=${encodeURIComponent(window.location.origin)}`
    : '';
  const copyPublicUrl = async () => {
    try { await navigator.clipboard.writeText(publicPageUrl); toast.success('Saite nokopēta'); }
    catch (e) { toast.error('Neizdevās nokopēt'); }
  };
  const downloadQr = async () => {
    try {
      const res = await axios.get(qrSrc, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kolv-qr-${cfg?.display_name ? cfg.display_name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : cfg?.id}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error('Lejupielāde neizdevās'); }
  };
  const [posterBusy, setPosterBusy] = useState(false);

  // --- New-order live notification (sound + toast) while the dashboard is open ---
  const audioCtxRef = useRef(null);
  const seenOrderIdsRef = useRef(null);
  useEffect(() => {
    const unlock = () => {
      try {
        if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
      } catch (e) { /* audio unsupported */ }
    };
    window.addEventListener('pointerdown', unlock);
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);
  const playChime = () => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    try {
      const now = ctx.currentTime;
      [880, 1175].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = freq;
        o.connect(g); g.connect(ctx.destination);
        const t = now + i * 0.18;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
        o.start(t); o.stop(t + 0.24);
      });
    } catch (e) { /* ignore */ }
  };
  useEffect(() => {
    if (activeTab !== 'manage' || !cfg?.id) return undefined;
    seenOrderIdsRef.current = null;  // re-baseline per page
    let stopped = false;
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/business-page/bookings/my`, { params: { page_id: cfg.id } });
        const bookings = data.bookings || [];
        // Alert on every NEW live request awaiting the owner — orders AND
        // reservations (table + service). `bookings/my` already excludes
        // unpaid online holds, so these are real, actionable requests.
        const pending = bookings.filter((b) => b.status === 'pending');
        const ids = new Set(pending.map((b) => b.id));
        if (seenOrderIdsRef.current === null) {
          seenOrderIdsRef.current = ids;  // first poll = baseline, no alert
          return;
        }
        const fresh = pending.filter((b) => !seenOrderIdsRef.current.has(b.id));
        seenOrderIdsRef.current = ids;
        if (fresh.length > 0) {
          playChime();
          const total = fresh.reduce((s, b) => s + (b.amount || 0), 0);
          const orders = fresh.filter((b) => b.kind === 'order');
          const tables = fresh.filter((b) => b.kind === 'table');
          const resv = fresh.filter((b) => b.kind !== 'order' && b.kind !== 'table');
          let label;
          if (fresh.length === 1) {
            label = orders.length ? 'Jauns pasūtījums!' : tables.length ? 'Jauns galdiņa pieprasījums!' : 'Jauna rezervācija!';
          } else {
            const parts = [];
            if (orders.length) parts.push(`${orders.length} ${orders.length === 1 ? 'pasūtījums' : 'pasūtījumi'}`);
            if (tables.length) parts.push(`${tables.length} galdiņa ${tables.length === 1 ? 'pieprasījums' : 'pieprasījumi'}`);
            if (resv.length) parts.push(`${resv.length} ${resv.length === 1 ? 'rezervācija' : 'rezervācijas'}`);
            label = `Jauni pieprasījumi — ${parts.join(', ')}`;
          }
          toast.success(`🔔 ${label}${total ? ` · €${total.toFixed(2)}` : ''}`, { duration: 9000 });
          setAllBookings(bookings);
          refetchStats();
        }
      } catch (e) { /* ignore poll errors */ }
    };
    check();
    const iv = setInterval(() => { if (!stopped) check(); }, 25000);
    return () => { stopped = true; clearInterval(iv); };
  }, [activeTab, cfg?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // CRM dashboard section navigation (overview / orders / store / stats / more).
  // setSection is wrapped so every switch also syncs the ?section= URL param,
  // letting a reload or shared link reopen the same view.
  const [section, setSectionState] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('section');
    return ['overview', 'orders', 'store', 'stats', 'settings'].includes(s) ? s : 'overview';
  });
  const setSection = (s) => {
    setSectionState(s);
    try {
      const u = new URL(window.location.href);
      if (s === 'overview') u.searchParams.delete('section'); else u.searchParams.set('section', s);
      window.history.replaceState(window.history.state, '', u.toString());
    } catch (e) { /* noop */ }
  };
  const initialSectionRef = useRef(new URLSearchParams(window.location.search).get('section'));
  const [ordersTab, setOrdersTab] = useState('all');
  const downloadPoster = async (size) => {
    if (!cfg?.id) return;
    setPosterBusy(true);
    try {
      const res = await axios.get(`${API}/business-page/me/pages/${cfg.id}/poster`, {
        params: { size, base: window.location.origin },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kolv-plakats-${cfg?.display_name ? cfg.display_name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() : cfg?.id}-${size}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error('Plakāta izveide neizdevās'); }
    finally { setPosterBusy(false); }
  };

  // Open a clickable stat detail modal, fetching the backing data on demand.
  const openStatDetail = async (type) => {
    setStatDetail(type);
    setDetailLoading(true);
    try {
      if (type === 'views') {
        const { data } = await axios.get(`${API}/business-page/views/my`, { params: { page_id: cfg?.id, days: 30 } });
        setViewsDetail(data);
      } else {
        const { data } = await axios.get(`${API}/business-page/bookings/my`, { params: { page_id: cfg?.id } });
        setAllBookings(data.bookings || []);
      }
    } catch (e) { toast.error('Neizdevās ielādēt detaļas'); }
    finally { setDetailLoading(false); }
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  const detailBookings = statDetail === 'upcoming'
    ? allBookings.filter((b) => b.status !== 'cancelled' && (b.date || '') >= todayIso)
    : statDetail === 'pending'
    ? allBookings.filter((b) => b.status === 'pending')
    : allBookings;

  // Load the full bookings list when the Orders ("Pasūtījumi") section is open.
  useEffect(() => {
    if (activeTab === 'manage' && section === 'orders' && cfg?.id) {
      axios.get(`${API}/business-page/bookings/my`, { params: { page_id: cfg.id } })
        .then(({ data }) => setAllBookings(data.bookings || []))
        .catch(() => {});
    }
  }, [activeTab, section, cfg?.id]);

  // Load the end-of-day summary when the Statistika section is open.
  useEffect(() => {
    if (activeTab === 'manage' && section === 'stats' && cfg?.id) fetchDailySummary(dailyDate);
  }, [activeTab, section, cfg?.id, dailyDate]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load sold gift cards when the Orders section is open (any template that
  // may have the gift-cards module enabled).
  useEffect(() => {
    if (activeTab === 'manage' && section === 'orders' && cfg?.id) loadSoldGiftCards();
  }, [activeTab, section, cfg?.id, cfg?.template]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Enriched footer data (contacts + requisites + socials with shop fallback) so
  // the dashboard footer preview matches the public page exactly. Owner viewing
  // their own page does NOT count as a visit (handled server-side).
  useEffect(() => {
    if (activeTab === 'manage' && cfg?.id && user?._id && !saving) {
      axios.get(`${API}/business-page/${user._id}/page/${cfg.id}`)
        .then(({ data }) => setFooterOwner(data.owner || null))
        .catch(() => setFooterOwner(null));
    }
  }, [activeTab, cfg?.id, user?._id, saving]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Orders tab filter (all / new=pending / processing=confirmed / done / cancelled).
  const ordersFiltered = allBookings.filter((b) => {
    const isOrd = b.kind === 'order';
    if (ordersTab === 'all') return true;
    if (ordersTab === 'new') return b.status === 'pending';
    if (ordersTab === 'processing') return ['confirmed', 'ready'].includes(b.status) && (isOrd || (b.date || '') >= todayIso);
    if (ordersTab === 'done') return b.status === 'completed' || (b.status === 'confirmed' && !isOrd && (b.date || '') < todayIso);
    if (ordersTab === 'cancelled') return b.status === 'cancelled' || b.status === 'rejected';
    return true;
  });

  // What "products" means for this page model (used in quick actions / labels).
  const productWord = cfg?.template === 'menu' ? 'ēdienkartes pozīciju' : 'pakalpojumu';

  // Rough profile-completeness score for the Overview "store status" bar.
  const completeness = (() => {
    if (!cfg?.id) return 0;
    let done = 0; const total = 5;
    if (cfg?.display_name) done += 1;
    if (cfg?.display_logo_url) done += 1;
    if (cfg?.address) done += 1;
    const hasProducts = cfg?.template === 'menu'
      ? (mc?.categories || []).some((c) => (c.items || []).length > 0)
      : cfg?.template === 'business_card'
      ? !!(bc?.address || bc?.hours)
      : (rc?.services || []).length > 0;
    if (hasProducts) done += 1;
    if (cfg?.facebook || cfg?.instagram || cfg?.whatsapp) done += 1;
    return Math.round((done / total) * 100);
  })();

  const upload = async (file, cb) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      cb(data.url);
    } catch (e) { toast.error('Augšupielāde neizdevās'); } finally { setUploading(false); }
  };

  const [pauseBusy, setPauseBusy] = useState(false);
  const togglePause = async () => {
    const next = !cfg?.is_paused;
    setPauseBusy(true);
    try {
      const until = next ? ((cfg?.pause_until || '').slice(0, 10) || null) : null;
      await axios.put(`${API}/business-page/me/pause`, { paused: next, pause_until: until });
      setCfg((c) => ({ ...c, is_paused: next, pause_until: next ? c?.pause_until : null }));
      toast.success(next ? 'Biznesa lapa pauzēta — paslēpta no kartes' : 'Biznesa lapa atkal aktīva');
    } catch (e) { toast.error('Neizdevās mainīt statusu'); } finally { setPauseBusy(false); }
  };
  const saveBizSchedule = async (date) => {
    setCfg((c) => ({ ...c, pause_until: date }));
    if (!cfg?.is_paused) return;
    try {
      await axios.put(`${API}/business-page/me/pause`, { paused: true, pause_until: date || null });
      toast.success(date ? `Atsāksies automātiski ${date}` : 'Pauze bez termiņa');
    } catch (e) { toast.error('Neizdevās saglabāt datumu'); }
  };

  // Compact dashboard footer: open a support chat with the KoLV admin.
  const contactKolvSupport = async () => {
    try {
      const { data } = await axios.get(`${API}/support/admin-contact`);
      navigate(`/messages?to=${data.id}&name=${encodeURIComponent(data.name)}`);
    } catch (e) { navigate('/messages'); }
  };

  const __dash = { API, TEMPLATES, SECTIONS, accounting, activateTpl, activeTab, allBookings, autoSelectedRef, availPlugins, bc, calMode, cfg, completeness, copyPublicUrl, curCfg, dayDate, dayFilteredSlots, dayTotal, dayView, deletePage, detailBookings, detailLoading, downloadPoster, downloadQr, exportLoad, initialSectionRef, legalSame, li, loadData, loadDay, loadEnd, loadStart, loadWeek, loading, matchSpec, mc, mondayOf, movePage, navigate, onsiteAmount, onsiteBooking, onsiteBusy, onsiteMethod, openStatDetail, ordersFiltered, ordersTab, pages, pauseBusy, pausePage, payload, pluginOn, posterBusy, productWord, publicPageUrl, qrOpen, qrSrc, rc, refetchStats, refreshAccounting, refreshSellerAcct, regeneratePage, regenBusy, reloadPages, save, saveBizSchedule, saving, section, selectPage, sellerAcct, setAccounting, setAllBookings, setBc, setBookingStatus, setCalMode, setCfg, setCur, setDayDate, setDayView, setDetailLoading, setLoadData, setLoadEnd, setLoadStart, setLoading, setMc, setOnsiteAmount, setOnsiteBooking, setOnsiteBusy, setOnsiteMethod, setOrdersTab, setPages, setPauseBusy, setPosterBusy, setQrOpen, setRc, setSaving, setSection, setSectionState, setSellerAcct, setSettleBusy, setSettleChecking, setSpecFilter, setStatDetail, setStats, setStoreTab, setTab, setUploading, setViewsDetail, setWeekView, settleBusy, settleChecking, settleDebt, shiftDay, specColor, specFilter, specialistCount, statDetail, stats, storeTab, submitOnsite, tab, templateLabel, todayIso, togglePause, togglePlugin, upcomingFiltered, upload, uploading, user, viewsDetail, weekFilteredDays, weekTotal, weekView, panelSurface, panelAccent, savePanelTheme, dailyDate, setDailyDate, dailySummary, dailyLoading, fetchDailySummary, dailyEmailOn, toggleDailyEmail, soldGiftCards, giftCardsInfo, redeemGiftCard };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  return (
    <BusinessDashboardContext.Provider value={__dash}>
    <div className="min-h-screen" style={{ ...pageVars(panelSurface, panelAccent, 'reservations'), background: 'var(--bg-page)', color: 'var(--text-primary)', fontFamily: 'Manrope, sans-serif' }} data-testid="business-templates-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-28 md:pb-10">
        {adminBiz && (
          <div className="mb-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-center justify-between gap-3" data-testid="admin-manage-banner">
            <div className="flex items-center gap-3 min-w-0">
              <ShieldCheck className="w-5 h-5 text-amber-400 shrink-0" />
              <p className="text-sm text-amber-100 truncate">
                <span className="font-bold">Admin režīms.</span> Tu pārvaldi {adminName ? <span className="font-semibold">{adminName}</span> : 'cita lietotāja'} uzņēmuma lapas. Izmaiņas tiek saglabātas klienta kontā.
              </p>
            </div>
            <button onClick={() => navigate('/admin')} data-testid="admin-manage-exit"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-amber-500 text-black hover:bg-amber-400 transition-colors">
              <X className="w-4 h-4" /> Iziet
            </button>
          </div>
        )}
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--accent)] mb-2">Bizness</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Uzņēmuma lapa
        </h1>
        <p className="text-white/50 text-sm mb-6 max-w-2xl">
          {activeTab === 'type'
            ? 'Izvēlies un aktivizē sava biznesa lapas veidus. Vari aktivizēt vairākus — katram sava adrese un vadība.'
            : 'Pārvaldi savas lapas saturu, rezervācijas un statistiku.'}
        </p>

        {/* Team invitations + shared businesses (always visible) */}
        <BusinessTeamBanner />

        {/* Tabs: choose page type vs manage the page */}
        <div className="inline-flex items-center p-1 rounded-full bg-white/5 border border-white/10 mb-8" data-testid="business-tabs">
          <button onClick={() => setTab('type')}
            className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${activeTab === 'type' ? 'bg-[var(--accent)] text-[var(--accent-contrast)]' : 'text-white/60 hover:text-white'}`}
            data-testid="business-tab-type">Lapas veids</button>
          <button onClick={() => cfg?.id && (setTab('manage'), setSection('overview'))} disabled={!cfg?.id}
            className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${activeTab === 'manage' ? 'bg-[var(--accent)] text-[var(--accent-contrast)]' : 'text-white/60 hover:text-white'} ${!cfg?.id ? 'opacity-40 cursor-not-allowed' : ''}`}
            title={!cfg?.id ? 'Vispirms izvēlies lapu' : ''}
            data-testid="business-tab-manage">{cfg?.id ? `Vadība: ${cfg?.display_name || templateLabel(cfg)}` : 'Vadības panelis'}</button>
        </div>

        {/* Quick page switcher — jump between page dashboards without leaving the manage tab */}
        {activeTab === 'manage' && cfg?.id && pages.length > 1 && (
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1" data-testid="dashboard-page-switcher">
            <span className="text-xs text-white/40 shrink-0 mr-1">Pārslēgt lapu:</span>
            {pages.map((p) => {
              const active = p.id === cfg?.id;
              return (
                <button key={p.id} onClick={() => selectPage(p)}
                  className={`shrink-0 flex items-center gap-2 text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${active ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'bg-white/5 text-white/60 border-white/10 hover:text-white hover:border-white/20'}`}
                  data-testid={`switch-page-${p.id}`}>
                  {p.display_logo_url
                    ? <img src={p.display_logo_url} alt="" className="w-4 h-4 rounded object-contain" />
                    : <span className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold ${active ? 'bg-black/20' : 'bg-white/10 text-white/50'}`}>{(p.display_name || templateLabel(p)).charAt(0)}</span>}
                  <span className="max-w-[160px] truncate">{p.display_name || templateLabel(p)}</span>
                  {p.is_paused && <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-black/40' : 'bg-orange-400'}`} title="Pauzēta" />}
                </button>
              );
            })}
          </div>
        )}

        {/* CRM section navigation — desktop tabs (mobile uses the bottom bar) */}
        {activeTab === 'manage' && cfg?.id && (
          <div className="hidden md:flex items-center gap-1 mb-8 border-b border-white/10" data-testid="crm-section-nav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const on = section === s.id;
              return (
                <button key={s.id} onClick={() => setSection(s.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${on ? 'border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-white/50 hover:text-white'}`}
                  data-testid={`crm-nav-${s.id}`}>
                  <Icon className="w-4 h-4" /> {s.label}
                  {s.id === 'orders' && stats?.pending_count > 0 && (
                    <span className="ml-1 text-[10px] font-bold bg-[var(--accent)] text-[var(--accent-contrast)] rounded-full px-1.5 py-0.5 leading-none">{stats.pending_count > 99 ? '99+' : stats.pending_count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Overview header — store identity, quick actions, alerts, status */}
        <OverviewSection />

        {/* Section title for non-overview sections */}

        {/* Orders ("Pasūtījumi") — bookings list with status tabs */}
        <OrdersSection />

        {/* Owner stats dashboard — visits + upcoming reservations */}

        {/* QR code — scan-to-book deep link for this page */}
        {activeTab === 'manage' && section === 'store' && cfg?.template && (
          <div className="mb-6 -mx-1 px-1 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="store-subtabs">
            {STORE_TABS.map((st) => {
              const Icon = st.icon;
              const on = storeTab === st.id;
              return (
                <button key={st.id} type="button" onClick={() => setStoreTab(st.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all active:scale-95 ${on ? 'bg-[var(--accent)] text-[var(--accent-contrast)] shadow-[0_6px_20px_var(--accent-ring)]' : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10'}`}
                  data-testid={`store-subtab-${st.id}`}>
                  <Icon className="w-4 h-4" strokeWidth={on ? 2.4 : 2} /> {st.label}
                </button>
              );
            })}
          </div>
        )}

        <QrPanel />


        {/* Stats summary — KPI cards at the top of the Statistika section */}
        <StatsSection />

        {/* KoLV accounting — per-page commission (online collected / owed / settled) */}

        {/* Day calendar — bookings per time slot for a chosen date */}

        {/* Specialist load overview (phase 3) */}


        {/* Central settings — identity + contacts + requisites + motifs + domain */}
        <SettingsSection />

        {/* Team management — invite employees / co-owners, roles, transfer ownership */}
        {activeTab === 'manage' && section === 'team' && (
          <BusinessTeamSection hasPage={!!cfg?.id} pageId={cfg?.id} pageLabel={cfg?.display_name || templateLabel(cfg)} />
        )}


        {/* Picker */}
        <TemplatePicker />

        {/* ============================= Reservations editor ===================== */}
        {activeTab === 'manage' && section === 'store' && cfg?.template === 'reservations' && (
          <ReservationsEditor storeTab={storeTab} rc={rc} setRc={setRc} mc={mc} setMc={setMc} upload={upload} uploading={uploading} pluginOn={pluginOn} togglePlugin={togglePlugin} availPlugins={availPlugins} />
        )}

        {/* ================================= Menu editor ========================= */}
        {activeTab === 'manage' && section === 'store' && cfg?.template === 'menu' && (
          <MenuEditor storeTab={storeTab} mc={mc} setMc={setMc} upload={upload} uploading={uploading} pluginOn={pluginOn} togglePlugin={togglePlugin} availPlugins={availPlugins} />
        )}

        {/* ============================ Business card editor ===================== */}
        {activeTab === 'manage' && section === 'store' && cfg?.template === 'business_card' && (
          <BusinessCardEditor storeTab={storeTab} bc={bc} setBc={setBc} upload={upload} uploading={uploading} pluginOn={pluginOn} togglePlugin={togglePlugin} availPlugins={availPlugins} />
        )}

        {activeTab === 'manage' && section === 'store' && cfg?.template && (
          <div className="sticky bottom-4 flex justify-end mt-8">
            <button onClick={save} disabled={saving}
              className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60"
              style={{ boxShadow: '0 8px 30px var(--accent-ring)' }} data-testid="save-business-page">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />} Saglabāt
            </button>
          </div>
        )}
      </div>

      {/* Business-page footer preview — the owner sees their own public footer
          (contacts + requisites + socials) right inside the dashboard. */}
      {activeTab === 'manage' && cfg?.id && (
        <div className="mt-6" data-testid="dashboard-company-footer">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <p className="text-white/40 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">Tavas lapas kājene</p>
          </div>
          <CompanyFooter
            theme="dark"
            name={footerOwner?.name || cfg?.display_name || templateLabel(cfg)}
            logo={footerOwner?.logo || cfg?.display_logo_url}
            contacts={footerOwner?.contacts || {
              phone: cfg?.contact_phone, email: cfg?.contact_email, website: cfg?.contact_website,
              address: cfg?.address,
              facebook: cfg?.facebook, instagram: cfg?.instagram, tiktok: cfg?.tiktok, youtube: cfg?.youtube, whatsapp: cfg?.whatsapp,
            }}
            requisites={footerOwner?.requisites || (legalSame ? {} : (li || {}))}
          />
        </div>
      )}

      {/* Compact dashboard footer — help + KoLV support (not the public marketing footer) */}
      <footer className="border-t border-white/10 mt-4 px-4 sm:px-6 py-6 pb-28 md:pb-6" data-testid="dashboard-footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
          <p className="text-white/40">© {new Date().getFullYear()} KoLV · Biznesa vadības panelis</p>
          <div className="flex items-center gap-5">
            <a href="/maksas-un-komisijas" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white inline-flex items-center gap-1.5 transition-colors" data-testid="dashboard-help-link">
              <HelpCircle className="w-4 h-4" /> Palīdzība
            </a>
            <button onClick={contactKolvSupport} className="text-[var(--accent)] hover:underline inline-flex items-center gap-1.5 font-semibold" data-testid="dashboard-support-btn">
              <MessageSquare className="w-4 h-4" /> Sazināties ar KoLV atbalstu
            </button>
          </div>
        </div>
      </footer>

      {/* Mobile CRM bottom navigation (replaces the global bar inside the panel) */}
      {activeTab === 'manage' && cfg?.id && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[var(--bg-header)] backdrop-blur-xl border-t border-white/10" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="crm-bottom-nav">
          <div className="flex items-stretch justify-around">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const on = section === s.id;
              return (
                <button key={s.id} onClick={() => setSection(s.id)}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors active:scale-95 ${on ? 'text-[var(--accent)]' : 'text-white/50'}`}
                  data-testid={`crm-bottomnav-${s.id}`}>
                  <span className="relative">
                    <Icon className="w-[22px] h-[22px]" strokeWidth={on ? 2.4 : 1.9} />
                    {s.id === 'orders' && stats?.pending_count > 0 && (
                      <span className="absolute -top-1.5 -right-2.5 text-[9px] font-bold bg-[var(--accent)] text-[var(--accent-contrast)] rounded-full px-1 leading-none py-0.5">{stats.pending_count > 99 ? '99+' : stats.pending_count}</span>
                    )}
                  </span>
                  <span className="text-[10px] font-medium leading-none">{s.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
    </BusinessDashboardContext.Provider>
  );
}

